import React, { Component, ReactElement } from 'react';
import { findIndex } from 'lodash';

interface CollapsibleGroupProps {
  children: React.ReactNode;
  open?: number;
  onStatusChange?: () => void;
}

interface CollapsibleGroupState {
  open: number | null;
  status: {
    index: number;
    title: string;
    completed: boolean;
    optional: boolean;
  }[];
}

interface CollapsibleChildProps {
  completed?: boolean;
  title?: string;
  optional?: boolean;
  id?: string;
  index?: number;
  open?: boolean;
  onToggle?: () => void;
  onClose?: (e: React.MouseEvent) => void;
  onComplete?: (index?: number) => void;
}

const getFirstUnfinished = (children: ReactElement<CollapsibleChildProps>[]): number => {
  return findIndex(children, (child) => child.props.completed !== true);
};

const getChildren = (children: React.ReactNode): ReactElement<CollapsibleChildProps>[] => {
  const childArray = React.Children.toArray(children) as ReactElement<CollapsibleChildProps>[];
  return childArray.filter((c) => !!c);
};

class CollapsibleGroupComponent extends Component<CollapsibleGroupProps, CollapsibleGroupState> {
  constructor(props: CollapsibleGroupProps) {
    super(props);

    const children = getChildren(props.children);

    this.state = {
      open: props.open ?? getFirstUnfinished(children),
      status: children.map((element, index) => ({
        index,
        title: element.props.title || '',
        completed: element.props.completed || false,
        optional: element.props.optional || false,
      })),
    };

    this.handleClose = this.handleClose.bind(this);
    this.handleGetStatus = this.handleGetStatus.bind(this);
  }

  componentDidMount() {
    const { open } = this.state;
    const { children } = this.props;

    const childArray = React.Children.toArray(children) as ReactElement<CollapsibleChildProps>[];
    const targetIsWithinBounds = open !== null && open >= 0 && open < childArray.length;

    if (!targetIsWithinBounds) return;

    const targetId = childArray[open].props.id;
    if (!targetId) return;

    const element = document.getElementById(targetId);
    if (element) element.scrollIntoView();
  }

  handleClose = (e: React.MouseEvent) => {
    const { open } = this.state;
    const { children } = this.props;

    e.stopPropagation();

    const childrenCount = getChildren(children).length;
    this.setState({ open: childrenCount - 1 === open ? null : (open ?? -1) + 1 });
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  handleGetStatus() {
    return this.state.status;
  }

  handleComplete = (index?: number) => {
    const { onStatusChange } = this.props;

    if (index !== undefined && this.state.status[index]) {
      this.state.status[index].completed = true;

      if (onStatusChange) onStatusChange();
    }
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  handleGoToStep(index: number) {
    this.setState({ open: index });
  }

  render() {
    const { children } = this.props;

    const elements = getChildren(children).map(
      (element, index) => element && React.cloneElement(element, {
        index,
        open: index === this.state.open,
        onToggle: () => this.setState({ open: this.state.open === index ? null : index }),
        onClose: this.handleClose,
        onComplete: this.handleComplete,
      })
    );

    return <div className="CollapsableGroup">{elements}</div>;
  }
}

export default CollapsibleGroupComponent;



